<template>
  <div
    ref="mapWrapper"
    :class="`${theme.current.value.dark ? 'bg-dark' : 'bg-white'}`"
    class="position-relative d-flex flex-column"
    style="overflow-x: hidden"
    @click="$emit('interact')"
  >
    <div class="flex-grow-1 d-flex flex-column relative-position">
      <google-map
        ref="mapReference"
        :key="`MAP-${config.public.GOOGLE_MAP_ID}:${theme.current.value.dark ? 'DARK' : 'LIGHT'}`"
        :map-id="config.public.GOOGLE_MAP_ID!"
        :api-promise="$googleApiPromise as any"
        :color-scheme="theme.current.value.dark ? 'DARK' : 'LIGHT'"
        class="w-100 flex-grow-1"
        :map-type-id="mapType"
        :fullscreen-control="false"
        :map-type-control="false"
        :zoom-control="false"
        :camera-control="false"
        :gesture-handling="readOnly ? 'none' : 'auto'"
        :clickable-icons="false"
        :scroll-wheel="!readOnly"
        :disable-double-click-zoom="!readOnly"
        :keyboard-shortcuts="!readOnly"
        @wheel="$emit('interact')"
      >
        <template v-if="currentZoom < clusterCutoffLimit">
          <sam-google-maps-cluster
            v-for="cluster of clusters"
            :key="cluster.name"
            :cluster="cluster"
            @click-cluster="zoomOnCluster(cluster)"
          />
        </template>

        <sam-google-maps-geo-asset-marker
          v-for="asset in visibleAssets"
          :key="asset._id"
          :asset="asset"
          :main-asset-id="mainAsset?._id"
          :selected-asset-id="selectedAsset?._id"
          @click="setSelectedAsset(asset)"
        />

        <template v-if="showRelationLines">
          <google-map-polyline
            v-for="relation in selectedAssetRelations"
            :key="relation._id"
            :options="{
              path: assetRelationToPath(relation),
              strokeColor: theme.current.value.dark
                ? parseColorToCSS(theme.current.value.colors.primary)
                : parseColorToCSS(theme.current.value.colors.tertiary),
              strokeOpacity: 1.0,
              strokeWeight: 1,
            }"
          />
          <template v-if="showMainAssetRelations !== false">
            <google-map-polyline
              v-for="relation in mainAssetRelations"
              :key="relation._id"
              :options="{
                path: assetRelationToPath(relation),
                strokeColor: theme.current.value.dark
                  ? parseColorToCSS(theme.current.value.colors.primary)
                  : parseColorToCSS(theme.current.value.colors.tertiary),
                strokeOpacity: 1.0,
                strokeWeight: 1,
              }"
            />
          </template>
        </template>

        <slot />

        <advanced-marker
          v-if="picker && pickerCoordinates"
          :options="{
            position: pickerCoordinates,
            gmpDraggable: !readOnly,
          }"
          @dragend="onPickerDragEnd"
        />

        <sam-google-maps-current-location-marker v-if="geolocationEnabled" />
      </google-map>

      <template v-if="!hideControls">
        <div class="position-absolute d-flex" style="margin: 10px; left: 0; top: 0">
          <div class="d-flex flex-column align-start" style="gap: 8px">
            <v-tooltip :text="$t('map.layers')" location="end">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-if="geoLayers.length || geoLayersDuoPP.length"
                  v-bind="tooltipProps"
                  :color="mapControlTab === MapControlTab.LAYERS ? 'primary' : 'surface'"
                  rounded="sm"
                  density="compact"
                  icon
                  :aria-label="$t('map.layers')"
                  style="width: 40px; min-height: 40px"
                  @click="() => (mapControlTab = mapControlTab === MapControlTab.LAYERS ? MapControlTab.CONTROLS : MapControlTab.LAYERS)"
                >
                  <v-badge
                    v-if="geoLayers.some((layer) => layer.enabled) || geoLayersDuoPP.some((layer) => layer.enabled)"
                    dot
                    color="error"
                  >
                    <v-icon icon="layers" />
                  </v-badge>

                  <v-icon v-else icon="layers" />
                </v-btn>
              </template>
            </v-tooltip>

            <v-tooltip v-if="!supplierStore.isLoggedInOnSupplier" :text="$t('assets.title')" location="end">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  :color="mapControlTab === MapControlTab.FILTERS ? 'primary' : 'surface'"
                  rounded="sm"
                  density="compact"
                  icon
                  :aria-label="$t('assets.title')"
                  style="width: 40px; min-height: 40px"
                  @click="mapControlTab = mapControlTab === MapControlTab.FILTERS ? MapControlTab.CONTROLS : MapControlTab.FILTERS"
                >
                  <v-badge v-if="assetCategories.length > 0 || cityFilters.length > 0" dot color="error">
                    <v-icon icon="$asset" />
                  </v-badge>

                  <v-icon v-else icon="$asset" />
                </v-btn>
              </template>
            </v-tooltip>

            <v-tooltip :text="showNearby ? $t('map.hideNearby') : $t('map.showNearby')" location="end">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-if="mainAsset || !disableShowNearby"
                  v-bind="tooltipProps"
                  color="surface"
                  rounded="sm"
                  density="compact"
                  icon
                  :aria-label="showNearby ? $t('map.hideNearby') : $t('map.showNearby')"
                  style="width: 40px; min-height: 40px"
                  @click="() => (showNearby = !showNearby)"
                >
                  <v-icon :icon="showNearby ? 'explore' : 'explore_off'" />
                </v-btn>
              </template>
            </v-tooltip>

            <v-tooltip :text="$t('map.type')" location="end">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  color="surface"
                  rounded="sm"
                  density="compact"
                  icon
                  :aria-label="$t('map.type')"
                  style="width: 40px; min-height: 40px"
                  @click="changeMapType"
                >
                  <v-icon :icon="mapType === 'roadmap' ? 'landscape' : 'satellite'" />
                </v-btn>
              </template>
            </v-tooltip>

            <v-tooltip :text="$t('map.recenter')" location="end">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  color="surface"
                  rounded="sm"
                  density="compact"
                  icon
                  :aria-label="$t('map.recenter')"
                  style="width: 40px; min-height: 40px"
                  @click="recenter"
                >
                  <v-icon icon="my_location" />
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </div>

        <div class="position-absolute" style="margin: 10px; top: 0; right: 0">
          <asset-info
            v-if="selectedAsset"
            :id="selectedAsset._id"
            :distance="'distance' in selectedAsset ? selectedAsset.distance : undefined"
            @close="() => (selectedAsset = null)"
          />
          <span v-else class="d-flex flex-wrap" style="gap: 8px">
            <slot name="absolute-top-right" />

            <v-tooltip :text="$t('fullscreen')" location="start">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-if="!hideControls && fullscreenCapable"
                  v-bind="tooltipProps"
                  color="surface"
                  rounded="sm"
                  density="compact"
                  icon="fullscreen"
                  :aria-label="$t('fullscreen')"
                  style="width: 40px; min-height: 40px"
                  @click="() => toggleFullscreen()"
                />
              </template>
            </v-tooltip>
          </span>
        </div>

        <div class="position-absolute" style="margin: 10px; margin-bottom: 75px; bottom: 0; right: 0">
          <v-card rounded="sm">
            <v-btn
              color="surface"
              rounded="sm"
              density="compact"
              icon
              :aria-label="$t('googleMaps.zoomIn')"
              style="min-width: 40px; min-height: 40px"
              @click="() => changeZoom(1)"
            >
              <v-icon icon="add" />
            </v-btn>

            <v-divider />

            <v-btn
              color="surface"
              rounded="sm"
              density="compact"
              icon
              :aria-label="$t('googleMaps.zoomOut')"
              style="min-width: 40px; min-height: 40px"
              @click="() => changeZoom(-1)"
            >
              <v-icon icon="remove" />
            </v-btn>
          </v-card>
        </div>

        <side-drawer
          :title="$t('map.layers')"
          :model-value="mapControlTab === MapControlTab.LAYERS"
          style="filter: opacity(0.95)"
          @update:model-value="() => (mapControlTab = MapControlTab.CONTROLS)"
        >
          <v-list :opened="['general', 'geoLayersDuoPP', 'geoLayers']">
            <v-list-group value="general">
              <template #activator="{ props: itemProps }"> <v-list-item v-bind="itemProps" :title="$t('general')" /> </template>

              <template #default>
                <v-list-item class="py-0">
                  <v-checkbox v-model="showRelationLines" density="comfortable" hide-details :label="$t('map.showRelationLines')" />
                </v-list-item>
              </template>
            </v-list-group>

            <v-list-group v-if="settings?.geoLayersDuoPP" value="geoLayersDuoPP">
              <template #activator="{ props: itemProps }">
                <v-list-item v-bind="itemProps" :title="'DuoPP GWSW'" />
              </template>

              <template #default>
                <v-list-item v-for="({ layer, enabled, legendUrl }, index) of geoLayersDuoPP" :key="layer as string" class="py-0">
                  <v-checkbox
                    v-model="geoLayersDuoPP[index]!.enabled"
                    hide-details
                    density="comfortable"
                    :label="$te(`geoLayersDuoPP.${layer}`) ? $t(`geoLayersDuoPP.${layer}`) : (layer as string)"
                  />
                  <v-img v-if="enabled && legendUrl" :src="legendUrl as string" :alt="`Legend: ${layer}`" width="auto" />
                </v-list-item>
              </template>
            </v-list-group>

            <v-list-group v-if="settings?.geoLayers" value="geoLayers">
              <template #activator="{ props: itemProps }">
                <v-list-item v-bind="itemProps" :title="'PDOK'" />
              </template>

              <template #default>
                <v-list-item v-for="({ layer, enabled, legendUrl }, index) of geoLayers" :key="layer as string" class="py-0">
                  <v-checkbox
                    v-model="geoLayers[index]!.enabled"
                    hide-details
                    density="comfortable"
                    :label="$te(`geoLayers.${layer}`) ? $t(`geoLayers.${layer}`) : (layer as string)"
                  />
                  <v-img v-if="enabled && legendUrl" :src="legendUrl as string" :alt="`Legend: ${layer}`" width="auto" />
                </v-list-item>
              </template>
            </v-list-group>
          </v-list>
        </side-drawer>

        <side-drawer
          :title="$t('assets.title')"
          :model-value="mapControlTab === MapControlTab.FILTERS"
          style="filter: opacity(0.95)"
          @update:model-value="() => (mapControlTab = MapControlTab.CONTROLS)"
        >
          <slot name="filter" :assets="assets">
            <v-list :opened="['categories', 'cities']">
              <v-list-group value="categories">
                <template #activator="{ props: itemProps }">
                  <v-list-item v-bind="itemProps">
                    <template #title>
                      <span>{{ $t("categories") }}</span>
                      <v-badge v-if="assetCategories.length > 0" inline color="error" :content="assetCategories.length" />
                    </template>
                  </v-list-item>
                </template>

                <template #default>
                  <v-list-item v-for="{ _id, description } of categories" :key="_id" class="py-0">
                    <v-checkbox v-model="assetCategories" hide-details density="comfortable" :label="description" :value="_id" />
                    <template #append>
                      <template v-if="showLoading">
                        <v-progress-circular indeterminate :size="16" :width="2" />
                      </template>
                      <template v-else> ({{ allAssets.filter((asset) => asset.category._id === _id).length }}) </template>
                    </template>
                  </v-list-item>
                </template>
              </v-list-group>

              <v-list-group value="cities">
                <template #activator="{ props: itemProps }">
                  <v-list-item v-bind="itemProps">
                    <template #title>
                      <span>{{ $t("cities") }}</span>
                      <v-badge v-if="cityFilters.length > 0" inline color="error" :content="cityFilters.length" />
                    </template>
                  </v-list-item>
                </template>

                <template #default>
                  <v-list-item v-for="city of cities" :key="city" class="py-0">
                    <v-checkbox v-model="cityFilters" hide-details density="comfortable" :label="city" :value="city" />
                    <template #append>({{ allAssets.filter((asset) => asset.location?.city === city).length }})</template>
                  </v-list-item>
                </template>
              </v-list-group>
            </v-list>
          </slot>
        </side-drawer>

        <side-drawer
          right
          :title="$t('map.layerFeatures')"
          style="filter: opacity(0.95)"
          :model-value="mapFeatures.length > 0"
          @update:model-value="() => (mapFeatures = [])"
        >
          <v-list>
            <v-list-group v-for="mapFeature in mapFeatures" :key="mapFeature.layerKey">
              <template #activator="{ props: itemProps }">
                <v-list-item v-bind="itemProps" :title="$t(`geoLayers.${mapFeature.layerKey}`)" />
              </template>

              <template #default>
                <div v-for="(feature, index) of mapFeature.features" :key="index">
                  <v-list-item
                    v-for="[propertyKey, propertyValue] of Object.entries(feature.properties).filter(([_key, value]) => !!value)"
                    :key="propertyKey"
                    :title="propertyKey"
                    :subtitle="propertyValue ? String(propertyValue) : undefined"
                  />
                </div>
              </template>
            </v-list-group>
          </v-list>
        </side-drawer>
      </template>
    </div>

    <v-overlay :model-value="showLoading" class="align-center justify-center always-show" persistent contained style="z-index: 998">
      <v-container class="d-flex justify-center align-center h-100 w-100 flex-column">
        <v-progress-circular indeterminate :size="64" :width="6" class="mb-3" />
        <p v-if="clustersLoading">{{ $t("map.loadingClusters") }}</p>
        <p v-else-if="assetsLoading">{{ $t("map.loadingAssets") }}</p>
        <p v-else>{{ $t("map.loadingData") }}</p>
      </v-container>
    </v-overlay>
  </div>
</template>

<script setup lang="ts">
import noop from "lodash-es/noop"
import { AdvancedMarker, GoogleMap, Polyline as GoogleMapPolyline } from "vue3-google-map"
import { useTheme } from "vuetify"

enum MapControlTab {
  CONTROLS = "CONTROLS",
  LAYERS = "LAYERS",
  FILTERS = "FILTERS",
}

const $route = useRoute()
const theme = useTheme()

const initialCenterOnLoad = ref(false)
const pickerCoordinates = defineModel<LatLngAlt>("pickerCoordinates")

const showNearby = defineModel<boolean>("showNearby", { default: false })
const mapFilter = defineModel<Record<string, FilterQueryValue>>("mapFilters", { default: () => {} })

const authStore = useAuthStore()
const organizationStore = useOrganizationStore()
const assetCategoryStore = useAssetCategoryStore()

const { organizations: currentOrganizationIds } = storeToRefs(authStore)
const supplierStore = useSupplierStore()

const { geolocationEnabled } = useUserPreferences()
const { $googleApiPromise } = useNuxtApp()
const config = useRuntimeConfig()

const emit = defineEmits<{
  (e: "dragged" | "interact"): void
}>()

const props = defineProps<{
  disableShowNearby?: boolean
  zoom?: number
  mainAsset?: LookupAsset
  showMainAssetRelations?: boolean
  assets?: Array<MapAsset>
  clusters?: Array<GeoCluster>
  // Generic loading before fetch map data is ready
  loading?: boolean
  // Loading of the clusters
  clustersLoading?: boolean
  // Loading of the assets
  assetsLoading?: boolean
  picker?: boolean
  hideControls?: boolean
  // disables click events like marker click, drag, etc
  readOnly?: boolean
}>()

const { mainAsset, showMainAssetRelations, assets, clusters, loading, clustersLoading, assetsLoading, picker, zoom, readOnly } =
  toRefs(props)

const forceInteract = () => {
  emit("interact")
}

const afterClusterUpdate = () => {
  if (mapControlTab.value === MapControlTab.FILTERS) {
    emit("interact")
  }
}

const { getDistinctValues } = useDataStore()

const { data: categories } = useLazyAsyncData("categories", () => assetCategoryStore.getCategories(), {
  default: () => [] as Array<AssetCategory>,
})

const { data: cities } = useLazyAsyncData(
  "cities",
  () => {
    return getDistinctValues("Asset", "location.city")
  },
  {
    default: () => [] as Array<string>,
  },
)
const { data: settings } = useLazyAsyncData("map-settings", () => organizationStore.getSettings(currentOrganizationIds.value![0]!))

const mapWrapper = ref<HTMLElement>()
const map = ref<google.maps.Map>()
const mapReference = useTemplateRef<InstanceType<typeof GoogleMap>>("mapReference")

const { initializeZoomControl, zoomOnCluster, currentZoom, changeZoom, clusterCutoffLimit } = useGoogleZoomControl(map, mainAsset)
const { initializeAssets, selectedAsset, selectedAssetRelations, mainAssetRelations, allAssets, setSelectedAsset } = useGoogleAssets(
  map,
  mainAsset,
  assets,
  showMainAssetRelations,
)
const { initializeBoundListener, visibleAssets } = useVisibleAssets(mapReference, map, allAssets, clusterCutoffLimit, currentZoom)
const { cityFilters, assetCategories } = useGoogleFilters(mapFilter, categories, cities)
const { initializePicker } = useGooglePicker(map, readOnly, picker, pickerCoordinates)

const onPickerDragEnd = async (e: { latLng: google.maps.LatLng }) => {
  pickerCoordinates.value = {
    lat: e.latLng.lat(),
    lng: e.latLng.lng(),
  }
}

// useViewPersistence is only for the dashboard, so add mock funtions if not on dashboard
let initializeViewPersistence: () => void = noop
let goToLastView: () => boolean = () => false

if ($route.name === "dashboard") {
  const viewPersistence = useViewPersistence(map)

  initializeViewPersistence = viewPersistence.initializeViewPersistence
  goToLastView = viewPersistence.goToLastView
}

// defineExpose cannot be called after an await so place a proxy function and overwrite it after the await
let recenterProxy: (...args: Array<unknown>) => void = noop
defineExpose({
  recenter: (...args: Array<unknown>) => recenterProxy(...args),
  initialCenterOnLoad,
})

const { recenter, updateClusterBounds } = await useGoogleRecenter(
  map,
  initialCenterOnLoad,
  zoom,
  clusterCutoffLimit,
  allAssets,
  clusters,
  picker,
  pickerCoordinates,
  forceInteract,
  afterClusterUpdate,
  goToLastView,
)
recenterProxy = recenter

const { initializeLayers, mapFeatures, geoLayers, geoLayersDuoPP } = useGoogleDataLayers(map, settings)

const loadedGoogle = (await $googleApiPromise) as typeof google

const mapType = ref<google.maps.MapTypeId>()
const changeMapType = () =>
  (mapType.value =
    mapType.value === loadedGoogle.maps.MapTypeId.ROADMAP ? loadedGoogle.maps.MapTypeId.HYBRID : loadedGoogle.maps.MapTypeId.ROADMAP)

const mapControlTab = ref(MapControlTab.CONTROLS)

const showRelationLines = useLocalStorage("show-relation-lines", true)

const toggleFullscreen = async () => {
  if (!fullscreenCapable.value) {
    return
  }

  if (typeof document !== "undefined" && document.fullscreenElement === mapWrapper.value) {
    await document.exitFullscreen()
  } else {
    await mapWrapper.value?.requestFullscreen()
  }
}

const fullscreenCapable = computed(() => {
  return typeof document !== "undefined" && document.fullscreenEnabled && mapWrapper.value
})

const initializeMap = async () => {
  if (!map.value) {
    throw new Error("Map is not initialized")
  }

  initializeAssets()
  initializeZoomControl()
  initializeBoundListener()
  initializeViewPersistence()
  initializeLayers()
  initializePicker()

  // If there are no clusters, watch for the first update and then update the cluster bounds
  if (!clusters.value?.length) {
    watch(
      () => clusters.value,
      () => {
        updateClusterBounds()
      },
      { once: true },
    )

    return
  }

  updateClusterBounds()
}

const { showLoading } = useShowMapLoading(loading, clustersLoading, assetsLoading, clusterCutoffLimit, currentZoom)

watch(
  [mapReference, () => mapReference.value?.ready],
  () => {
    if (!mapReference.value?.ready) {
      return
    }

    map.value = mapReference.value?.map
    // Google may not be initialized when the component is loaded
    mapType.value = loadedGoogle.maps.MapTypeId.ROADMAP

    initializeMap()
  },
  { immediate: true },
)

onUnmounted(() => {
  if (map.value) {
    map.value = undefined
  }
})
</script>
